import React from 'react'
import PropTypes from 'prop-types'
import Dropdown from 'components/Dropdown'
import { FieldLabel, useFieldIds } from './FieldLabel'
import ErrorText from './ErrorText'

const flattenOptions = (groups, options) =>
  groups.length ? groups.flatMap(group => group.options || []) : options

const OptionLabel = ({ option }) =>
  option.name != null && option.idLabel != null ? (
    <>
      {option.name}(<span className='se-option-id'>{option.idLabel}</span>)
    </>
  ) : (
    option.text
  )

const MenuItem = ({ option, selected, onChange }) => (
  <div
    role='option'
    aria-selected={selected}
    aria-disabled={!!option.disabled}
    data-closetrigger={option.disabled ? undefined : 'true'}
    className={`dropdown-item clickable${selected ? ' selected' : ''}${option.disabled ? ' is-disabled' : ''}`}
    onClick={event => {
      if (option.disabled) {
        event.stopPropagation()
        return
      }
      onChange(option.key)
    }}>
    <OptionLabel option={option} />
  </div>
)

MenuItem.propTypes = {
  option: PropTypes.object,
  selected: PropTypes.bool,
  onChange: PropTypes.func,
}

const GroupedSelect = ({
  label,
  ariaLabel,
  value,
  groups = [],
  options = [],
  onChange,
  error,
  disabled,
  hint,
  className,
  placeholder = 'Select…',
}) => {
  const id = useFieldIds('select')
  const hasGroups = groups.length > 0
  const flatOptions = flattenOptions(groups, options)
  const selectedKey = value == null ? '' : String(value)
  const selectedOption = flatOptions.find(option => String(option.key) === selectedKey)

  return (
    <div className={['se-field', className].filter(Boolean).join(' ')} aria-label={ariaLabel || undefined}>
      {label ? (
        <FieldLabel htmlFor={id} hint={hint}>
          {label}
        </FieldLabel>
      ) : null}
      <Dropdown
        id={id}
        className='se-dropdown'
        selected={selectedKey}
        main={selectedOption ? <OptionLabel option={selectedOption} /> : undefined}
        placeholder={placeholder}
        disabled={disabled}
        hasError={!!error}
        options={flatOptions.map(option => ({
          ...option,
          key: String(option.key),
          value: String(option.key),
        }))}
        onSelect={item => {
          if (item?.disabled) return
          onChange(item.key)
        }}>
        {hasGroups
          ? groups.map(group => (
              <div key={group.label} className='se-dropdown-section'>
                <div className='se-dropdown-group'>{group.label}</div>
                {(group.options || []).map(option => (
                  <MenuItem
                    key={option.key}
                    option={option}
                    selected={String(option.key) === selectedKey}
                    onChange={onChange}
                  />
                ))}
              </div>
            ))
          : flatOptions.map(option => (
              <MenuItem
                key={option.key}
                option={option}
                selected={String(option.key) === selectedKey}
                onChange={onChange}
              />
            ))}
      </Dropdown>
      <ErrorText message={error} />
    </div>
  )
}

GroupedSelect.propTypes = {
  label: PropTypes.string,
  ariaLabel: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groups: PropTypes.array,
  options: PropTypes.array,
  onChange: PropTypes.func,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  hint: PropTypes.node,
  className: PropTypes.string,
  placeholder: PropTypes.string,
}

export default GroupedSelect
